import { MEMBERSHIP_NAMES } from '@services/MarppleShop/features/Membership/constant';
import { MembershipConstantS } from '@services/MarppleShop/features/Membership/outbound/share';
import {
  MembershipAdjustmentAtList,
  MembershipAdjustmentServiceType,
} from '@services/MarppleShop/features/Membership/type';
import { StudioHeader } from '@services/MarppleShop/marppleShopStudioApp/components/cells/Layout/StudioTitle.ts';
import { MconAdjustmentCore } from '@services/MarppleShopStudio/features/core/adjustment/mcon-adjustment.core.ts';
import { MconAdjustmentForStudio } from '@services/MarppleShopStudio/features/core/adjustment/mcon-adjustment.ts';
import format from 'date-fns/format/index.js';

import { html } from 'fxjs/es';
import { html as runeHtml } from 'rune-ts';
import { GoodsTypeTmplS } from '../../../GoodsType/S/Tmpl/module/GoodsTypeTmplS.js';
import { MShopUtilConstantS } from '../../../MShop/Util/S/Constant/module/MShopUtilConstantS.js';
import { UtilS } from '../../../Util/S/Function/module/UtilS.js';
import { messages } from '../../Setting/S/messages_id.js';
import { makeSellerStudioTable } from '../../Setting/S/table.ts';
import { sumAdjustments, sumMembershipAdjustments } from './util';

const makeSellerAdjustmentTable = ({ adjustments, crew }) => {
  const { quantity, profit, bonus_profit, total_profit, without_fee_amount, pg_amount } =
    sumAdjustments(adjustments);

  const bonus_profit_tooltip_content = html`
    <span class="lnr tooltip-icon tooltip-question"></span>
    <div class="tooltip-container">
      <h4>${T(messages.table.th7)}</h4>
      <p>${T(messages.tooltip.t1)}</p>
    </div>
  `;

  const pg_amount_tooltip_content = html`
    <span class="lnr tooltip-icon tooltip-question"></span>
    <div class="tooltip-container">
      <h4>${ET('mps2::studio::tooltip::pg_commission_title')}</h4>
      <p>${ET('mps2::studio::tooltip::pg_commission_desc')}</p>
    </div>
  `;

  const without_fee_amount_tooltip_content = html`
    <span class="lnr tooltip-icon tooltip-question"></span>
    <div class="tooltip-container">${T(messages.tooltip.t2)}</div>
  `;

  return makeSellerStudioTable({
    col_rates: !crew.without_bonus_profit
      ? [
          { rate: 11, align: 'left' },
          { rate: 25, align: 'center', klass: 'seller-studio-table__td--text-overflow' },
          { rate: 10 },
          { rate: 10 },
          {
            rate: 12,
            klass: 'bonus-profit-service',
            tool_tip: bonus_profit_tooltip_content,
          },
          { rate: 10 },
          {
            rate: 12,
            tool_tip: pg_amount_tooltip_content,
          },
          {
            rate: 12,
            tool_tip: without_fee_amount_tooltip_content,
          },
          { rate: 10, align: 'center' },
        ]
      : [
          { rate: 11, align: 'left' },
          { rate: 25, align: 'center' },
          { rate: 16 },
          { rate: 16 },
          null,
          null,
          {
            rate: 16,
            tool_tip: without_fee_amount_tooltip_content,
          },
          { rate: 16, align: 'center' },
        ],
    headers: [
      T(messages.table.th15),
      T(messages.table.th12),
      T(messages.table.th4),
      T(messages.table.th6),
      T(messages.table.th7),
      T(messages.table.th8),
      T(messages.table.th16_1),
      T(messages.table.th16),
      T(messages.table.th17),
    ],
    bodys: adjustments.map(
      ({
        created_at,
        type,
        p_name,
        bp_name,
        quantity,
        profit,
        bonus_profit,
        without_fee_amount,
        pg_amount,
      }) => {
        return [
          format(created_at, 'yyyy-MM-dd'),
          p_name || bp_name,
          UtilS.commify(quantity),
          UtilS.commify(profit),
          UtilS.commify(bonus_profit),
          UtilS.commify(profit + bonus_profit),
          UtilS.commify(pg_amount),
          UtilS.commify(without_fee_amount),
          T(messages.adjustments[type]),
        ];
      },
    ),
    totals: [
      T(messages.table.th18),
      '',
      UtilS.commify(quantity),
      UtilS.commify(profit),
      UtilS.commify(bonus_profit),
      UtilS.commify(total_profit),
      UtilS.commify(pg_amount),
      UtilS.commify(without_fee_amount),
      '-',
    ],
  });
};

export const makeAdjustmentsSettingsBodyHtml = ({
  adjustments,
  date,
  crew,
  goods_type_id,
  has_permission_tpl,
  domain_name,
  is_membership_available,
}) => {
  const is_afreeca = crew.id == MShopUtilConstantS.SOOP_MARPPLESHOP_CREW_ID;
  const preparing_adjustments = !!adjustments?.length;

  return html`
    <div class="header flex">
      <div class="left">
        ${new StudioHeader({
          title: UtilS.isMarppleShopApp()
            ? ET('mps2::studio::page::adjustments', {
                date: format(date, T(messages.adjustments.ym_locale)),
              })
            : `${T(messages.menus.adjustments)} - ${format(date, T(messages.adjustments.ym_locale))}`,
          description: UtilS.isMarppleShopApp()
            ? ET('mps2::studio::page::adjustments_guide')
            : T(messages.adjustments.p1),
          with_margin: false,
          suffix: {
            type: 'tooltip',
            tooltip_content: runeHtml`<p>
                ${runeHtml.preventEscape(
                  UtilS.isMarppleShopApp()
                    ? ET('mps2::studio::tooltip::adjustments', {
                        tpl_adjustments: has_permission_tpl ? ` & ${T(messages.menus.tpl_adjustments)} ` : '',
                      })
                    : T(messages.tooltip.h2, {
                        tpl_adjustments: has_permission_tpl ? ` & ${T(messages.menus.tpl_adjustments)} ` : '',
                      }),
                )}
              </p>`,
          },
        }).toHtmlSSR()}
      </div>
      <div class="right">
        <button
          type="button"
          class="download-excel${preparing_adjustments ? '' : ' download-excel--disabled'}"
          ${preparing_adjustments ? '' : 'disabled'}
        >
          <img
            src="${preparing_adjustments
              ? '//s3.marpple.co/files/u_29089/2023/2/original/8e3f4cc22c165c2cdfa3a9fc5af59b753975d1ac1.webp'
              : '//s3.marpple.co/files/u_29089/2023/2/original/9794c9df1bd62bfa1d2467dd05c1e924beb989851.webp'}"
            alt=""
            style="pointer-events:${preparing_adjustments ? 'auto' : 'none'}"
          />
          ${T('item_order_excel::정산 내역 내려받기')}
        </button>
        <button
          class="btn btn-blue"
          name="date"
          type="button"
          value="${format(date, T(messages.adjustments.ym_digit))}"
        >
          <i class="lnr lnr-calendar-full"></i>
          ${T(messages.adjustments.btn1)}
        </button>
      </div>
    </div>
    ${!is_afreeca
      ? GoodsTypeTmplS.adjustmentGoodsTypeHref({
          has_permission_tpl,
          goods_type_id,
          domain_name,
          is_membership_available,
        })
      : ''}
    <div class="body">${makeSellerAdjustmentTable({ adjustments, crew })}</div>
  `;
};

export const makeMembershipAdjustmentTableData = ({
  adjustments,
  service,
}: {
  adjustments: MembershipAdjustmentAtList[];
  service: MembershipAdjustmentServiceType;
}) => {
  const is_membership = service == MembershipConstantS.MEMBERSHIP; // false 이면 support 후원
  const is_support = service == MembershipConstantS.SUPPORT;
  const adjustment_total = sumMembershipAdjustments(adjustments);

  return {
    header: [
      T(messages.table.th45),
      T(messages.table.th15),
      T(is_membership ? messages.table.th46 : is_support ? T(messages.table.th47) : T(messages.table.th48)),
      T(messages.table.th6),
      T(messages.table.th16_2),
      T(messages.table.th16_1),
      T(messages.table.th16),
      T(messages.table.th36),
    ],
    body_rows: adjustments.map((adjustment, idx) => {
      return [
        idx + 1,
        format(adjustment.created_at, 'yyyy-MM-dd'),
        is_membership
          ? adjustment.membership_name
          : is_support
            ? adjustment.support_name
            : adjustment.commission_name,
        UtilS.commify(adjustment.amount),
        UtilS.commify(adjustment.fee),
        UtilS.commify(adjustment.pg_amount),
        UtilS.commify(adjustment.without_commission_amount),
        T(messages.adjustments[adjustment.type.toLowerCase() as 'payment' | 'refund']),
      ];
    }),
    total: [
      T(messages.table.th18),
      '',
      '',
      UtilS.commify(adjustment_total.amount),
      UtilS.commify(adjustment_total.fee),
      UtilS.commify(adjustment_total.pg_amount),
      UtilS.commify(adjustment_total.without_commission_amount),
      '',
    ],
  };
};

const makeStudioMembershipAdjustmentTable = ({
  adjustments,
  service,
}: {
  adjustments: MembershipAdjustmentAtList[];
  service: MembershipAdjustmentServiceType;
}) => {
  const pg_amount_tooltip_content = html`
    <span class="lnr tooltip-icon tooltip-question"></span>
    <div class="tooltip-container">
      <h4>${ET('mps2::studio::tooltip::pg_commission_title')}</h4>
      <p>${ET('mps2::studio::tooltip::pg_commission_desc')}</p>
    </div>
  `;

  const without_fee_amount_tooltip_content = html`
    <span class="lnr tooltip-icon tooltip-question"></span>
    <div class="tooltip-container">
      <h4>${T(MEMBERSHIP_NAMES[service])}</h4>
      <p>${T(messages.tooltip.t3)}</p>
    </div>
  `;

  const { body_rows, header, total } = makeMembershipAdjustmentTableData({ adjustments, service });

  return makeSellerStudioTable({
    col_rates: [
      { rate: 5.05, align: 'center' },
      { rate: 10.1, align: 'center', klass: 'seller-studio-table__td--text-overflow' },
      { align: 'center' },
      { rate: 10.1 },
      {
        rate: 10.1,
      },
      { rate: 10.1, tool_tip: pg_amount_tooltip_content },
      {
        rate: 10.1,
        tool_tip: without_fee_amount_tooltip_content,
      },
      {
        rate: 10.1,
        align: 'center',
      },
    ],
    bodys: body_rows,
    headers: header,
    totals: total,
  });
};

export const makeMembershipAdjustmentsSettingsBodyHtml = ({
  adjustments,
  date,
  crew,
  has_permission_tpl,
  domain_name,
  menu_name,
  is_membership_available,
}: {
  adjustments: MembershipAdjustmentAtList[];
  domain_name: string;
  menu_name: MembershipAdjustmentServiceType;
  date: Date;
  crew: { id: number };
  has_permission_tpl: boolean;
  is_membership_available: boolean;
}) => {
  const is_afreeca = crew.id == MShopUtilConstantS.SOOP_MARPPLESHOP_CREW_ID;
  const preparing_adjustments = !!adjustments?.length;

  return html`
    <div class="header flex">
      <div class="left">
        ${new StudioHeader({
          title: UtilS.isMarppleShopApp()
            ? ET('mps2::studio::page::adjustments', {
                date: format(date, T(messages.adjustments.ym_locale)),
              })
            : `${T(messages.menus.adjustments)} - ${format(date, T(messages.adjustments.ym_locale))}`,
          description: UtilS.isMarppleShopApp()
            ? ET('mps2::studio::page::adjustments_guide')
            : T(messages.adjustments.p1),
          with_margin: false,
          suffix: {
            type: 'tooltip',
            tooltip_content: runeHtml`<p>
                ${runeHtml.preventEscape(
                  UtilS.isMarppleShopApp()
                    ? ET('mps2::studio::tooltip::adjustments', {
                        tpl_adjustments: has_permission_tpl ? ` & ${T(messages.menus.tpl_adjustments)} ` : '',
                      })
                    : T(messages.tooltip.h2, {
                        tpl_adjustments: has_permission_tpl ? ` & ${T(messages.menus.tpl_adjustments)} ` : '',
                      }),
                )}
              </p>`,
          },
        }).toHtmlSSR()}
      </div>
      <div class="right">
        <button
          data-service="${menu_name}"
          type="button"
          class="download-excel${preparing_adjustments ? '' : ' download-excel--disabled'}"
          ${preparing_adjustments ? '' : 'disabled'}
        >
          <img
            src="${preparing_adjustments
              ? '//s3.marpple.co/files/u_29089/2023/2/original/8e3f4cc22c165c2cdfa3a9fc5af59b753975d1ac1.webp'
              : '//s3.marpple.co/files/u_29089/2023/2/original/9794c9df1bd62bfa1d2467dd05c1e924beb989851.webp'}"
            alt=""
            style="pointer-events:${preparing_adjustments ? 'auto' : 'none'}"
          />
          ${T('item_order_excel::정산 내역 내려받기')}
        </button>
        <button
          class="btn btn-blue"
          name="date"
          type="button"
          value="${format(date, T(messages.adjustments.ym_digit))}"
        >
          <i class="lnr lnr-calendar-full"></i>
          ${T(messages.adjustments.btn1)}
        </button>
      </div>
    </div>
    ${!is_afreeca
      ? GoodsTypeTmplS.adjustmentGoodsTypeHref({
          has_permission_tpl,
          domain_name,
          menu_name,
          is_membership_available,
        })
      : ''}
    <div class="body">${makeStudioMembershipAdjustmentTable({ adjustments, service: menu_name })}</div>
  `;
};

export const makeMconAdjustmentTableData = ({ adjustments }: { adjustments: MconAdjustmentForStudio[] }) => {
  const adjustment_total = MconAdjustmentCore.sumAdjustments(adjustments);

  return {
    header: [
      T(messages.table.th45),
      T(messages.table.th15),
      ET('mps2::seller_studio::creator_design::theme_name'),
      T(messages.table.th6),
      T(messages.table.th16_2),
      T(messages.table.th16_1),
      T(messages.table.th16),
    ],
    body_rows: adjustments.map((adjustment, idx) => {
      return [
        idx + 1,
        format(adjustment.created_at, 'yyyy-MM-dd'),
        adjustment.name,
        UtilS.commify(adjustment.profit),
        UtilS.commify(adjustment.service_fee),
        UtilS.commify(adjustment.pg_fee),
        UtilS.commify(MconAdjustmentCore.calcWithoutCommissionAmount(adjustment)),
      ];
    }),
    total: [
      T(messages.table.th18),
      '',
      '',
      UtilS.commify(adjustment_total.amount),
      UtilS.commify(adjustment_total.fee),
      UtilS.commify(adjustment_total.pg_amount),
      UtilS.commify(adjustment_total.without_commission_amount),
      '',
    ],
  };
};

const makeMconAdjustmentTable = ({ adjustments }: { adjustments: MconAdjustmentForStudio[] }) => {
  const pg_amount_tooltip_content = html`
    <span class="lnr tooltip-icon tooltip-question"></span>
    <div class="tooltip-container">
      <h4>${ET('mps2::studio::tooltip::pg_commission_title')}</h4>
      <p>${ET('mps2::studio::tooltip::pg_commission_desc')}</p>
    </div>
  `;

  const { body_rows, header, total } = makeMconAdjustmentTableData({ adjustments });

  return makeSellerStudioTable({
    col_rates: [
      { rate: 5.05, align: 'center' },
      { rate: 10.1, align: 'center', klass: 'seller-studio-table__td--text-overflow' },
      { align: 'center' },
      { rate: 10.1 },
      {
        rate: 10.1,
      },
      { rate: 10.1, tool_tip: pg_amount_tooltip_content },
      {
        rate: 10.1,
      },
    ],
    bodys: body_rows,
    headers: header,
    totals: total,
  });
};

export const makeMconAdjustmentsSettingsBodyHtml = ({
  adjustments,
  date,
  crew,
  has_permission_tpl,
  domain_name,
  goods_type_id,
  is_membership_available,
}: {
  adjustments: MconAdjustmentForStudio[];
  domain_name: string;
  date: Date;
  crew: { id: number };
  goods_type_id: 'MCON';
  has_permission_tpl: boolean;
  is_membership_available: boolean;
}) => {
  const is_afreeca = crew.id == MShopUtilConstantS.SOOP_MARPPLESHOP_CREW_ID;
  const preparing_adjustments = !!adjustments?.length;

  return html`
    <div class="header flex">
      <div class="left">
        ${new StudioHeader({
          title: UtilS.isMarppleShopApp()
            ? ET('mps2::studio::page::adjustments', {
                date: format(date, T(messages.adjustments.ym_locale)),
              })
            : `${T(messages.menus.adjustments)} - ${format(date, T(messages.adjustments.ym_locale))}`,
          description: UtilS.isMarppleShopApp()
            ? ET('mps2::studio::page::adjustments_guide')
            : T(messages.adjustments.p1),
          with_margin: false,
          suffix: {
            type: 'tooltip',
            tooltip_content: runeHtml`<p>
                ${runeHtml.preventEscape(
                  UtilS.isMarppleShopApp()
                    ? ET('mps2::studio::tooltip::adjustments', {
                        tpl_adjustments: has_permission_tpl ? ` & ${T(messages.menus.tpl_adjustments)} ` : '',
                      })
                    : T(messages.tooltip.h2, {
                        tpl_adjustments: has_permission_tpl ? ` & ${T(messages.menus.tpl_adjustments)} ` : '',
                      }),
                )}
              </p>`,
          },
        }).toHtmlSSR()}
      </div>
      <div class="right">
        <button
          data-service="${goods_type_id}"
          type="button"
          class="download-excel${preparing_adjustments ? '' : ' download-excel--disabled'}"
          ${preparing_adjustments ? '' : 'disabled'}
        >
          <img
            src="${preparing_adjustments
              ? '//s3.marpple.co/files/u_29089/2023/2/original/8e3f4cc22c165c2cdfa3a9fc5af59b753975d1ac1.webp'
              : '//s3.marpple.co/files/u_29089/2023/2/original/9794c9df1bd62bfa1d2467dd05c1e924beb989851.webp'}"
            alt=""
            style="pointer-events:${preparing_adjustments ? 'auto' : 'none'}"
          />
          ${T('item_order_excel::정산 내역 내려받기')}
        </button>
        <button
          class="btn btn-blue"
          name="date"
          type="button"
          value="${format(date, T(messages.adjustments.ym_digit))}"
        >
          <i class="lnr lnr-calendar-full"></i>
          ${T(messages.adjustments.btn1)}
        </button>
      </div>
    </div>
    ${!is_afreeca
      ? GoodsTypeTmplS.adjustmentGoodsTypeHref({
          has_permission_tpl,
          domain_name,
          goods_type_id,
          is_membership_available,
        })
      : ''}
    <div class="body">${makeMconAdjustmentTable({ adjustments })}</div>
  `;
};
